import {
    EnvironmentOutlined,
    GiftOutlined,
    HistoryOutlined,
    SearchOutlined,
    StarFilled,
    WalletOutlined,
} from '@ant-design/icons';
import { Button, Card, Divider, Empty, Input, Modal, Select, Skeleton, Space, Tabs, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import useRewards from '@/shared/hooks/useRewards';

const { Title, Text, Paragraph } = Typography;

const RewardsCatalog = () => {
    const { rewards, history, options, balance, loading, historyLoading, filters, setFilters, redeem, fetchHistory } =
        useRewards();

    const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
    const [selectedReward, setSelectedReward] = useState(null);
    const [activeTab, setActiveTab] = useState('catalog');

    const handleRedeemClick = (reward) => {
        setSelectedReward(reward);
        setIsRedeemModalOpen(true);
    };

    const confirmRedeem = async () => {
        if (!selectedReward) return;
        const result = await redeem(selectedReward.id);
        if (result.success) {
            setIsRedeemModalOpen(false);
        }
    };

    const onSearch = (value) => {
        setFilters((prev) => ({ ...prev, search: value, page: 1 }));
    };

    const renderCatalog = () => {
        if (loading && rewards.length === 0) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Card key={i} className="rounded-2xl overflow-hidden border-0 shadow-sm">
                            <Skeleton.Image className="w-full h-48" active />
                            <Skeleton className="p-4" active />
                        </Card>
                    ))}
                </div>
            );
        }

        if (rewards.length === 0) {
            return (
                <div className="py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
                    <Empty description="No rewards available at the moment." />
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-4">
                {rewards.map((reward) => (
                    <Card
                        key={reward.id}
                        hoverable
                        className="group rounded-2xl overflow-hidden border-0 shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white"
                        cover={
                            <div className="relative h-48 overflow-hidden bg-gray-100">
                                {reward.attachments?.[0]?.url ? (
                                    <img
                                        alt={reward.name}
                                        src={reward.attachments[0].url}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                        <GiftOutlined style={{ fontSize: '48px' }} />
                                    </div>
                                )}
                                <div className="absolute top-3 right-3">
                                    <Tag
                                        color="gold"
                                        className="m-0 border-0 rounded-full px-3 py-1 font-semibold flex items-center gap-1 shadow-sm backdrop-blur-md bg-white/90"
                                    >
                                        <StarFilled /> {reward.points_required} Points
                                    </Tag>
                                </div>
                                {reward.owning_brand && (
                                    <div className="absolute bottom-3 left-3">
                                        <Tag
                                            color="blue"
                                            className="m-0 border-0 rounded-full px-3 py-0.5 text-xs font-medium backdrop-blur-md bg-white/80 text-blue-800"
                                        >
                                            <EnvironmentOutlined className="mr-1" /> {reward.owning_brand.name}
                                        </Tag>
                                    </div>
                                )}
                            </div>
                        }
                    >
                        <div className="flex flex-col h-full">
                            <div className="mb-2">
                                <Text strong className="text-lg line-clamp-1 text-gray-800">
                                    {reward.name}
                                </Text>
                                <Paragraph ellipsis={{ rows: 2 }} className="text-gray-500 text-sm mt-1 mb-4 h-10">
                                    {reward.description}
                                </Paragraph>
                            </div>

                            <Button
                                type="primary"
                                block
                                className="h-10 rounded-xl bg-blue-600 hover:bg-blue-700 border-0 font-medium tracking-wide shadow-md shadow-blue-200"
                                onClick={() => handleRedeemClick(reward)}
                                disabled={balance.current < reward.points_required}
                            >
                                {balance.current < reward.points_required ? 'Insufficient Points' : 'Redeem Now'}
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    const renderHistory = () => {
        if (historyLoading) {
            return <Skeleton active className="p-8 bg-white rounded-3xl" />;
        }

        if (history.length === 0) {
            return (
                <div className="py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
                    <Empty description="You haven't redeemed any rewards yet." />
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {history.map((item) => (
                    <Card key={item.id} className="rounded-2xl border-0 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-100">
                                {item.reward?.attachments?.[0]?.url ? (
                                    <img
                                        src={item.reward.attachments[0].url}
                                        alt={item.reward.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <GiftOutlined className="text-gray-300 text-2xl" />
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <Text strong className="text-lg">
                                            {item.reward?.name || 'Unknown Reward'}
                                        </Text>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <Text type="secondary" className="text-xs">
                                                Redeemed on {dayjs(item.created_at).format('MMM D, YYYY HH:mm')}
                                            </Text>
                                            <Tag
                                                color={item.status?.value === 'redeemed' ? 'success' : 'processing'}
                                                className="rounded-full px-2 text-[10px] uppercase border-0"
                                            >
                                                {item.status?.label || 'Processing'}
                                            </Tag>
                                        </div>
                                    </div>
                                    <Text strong className="text-blue-600">
                                        -{item.points_deducted} pts
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-8 pb-10">
            {/* Premium Points Header */}
            <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-800 p-8 lg:p-12 text-white shadow-2xl shadow-blue-200">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="flex-1">
                        <Space direction="vertical" size={2}>
                            <Text className="text-blue-100 font-medium tracking-widest uppercase text-xs">
                                Loyalty Program
                            </Text>
                            <Title level={1} className="m-0 text-white tracking-tight" style={{ color: 'white' }}>
                                Rewards Catalog
                            </Title>
                            <Text className="text-blue-100 opacity-80 max-w-md block mt-2">
                                Discover exclusive benefits and claim your perks using your earned reward points.
                            </Text>
                        </Space>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-inner flex items-center gap-5">
                            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl">
                                <WalletOutlined />
                            </div>
                            <div>
                                <Text className="text-blue-100 text-xs block mb-1">Available Balance</Text>
                                <div className="flex items-baseline gap-1">
                                    <Title level={2} className="m-0 text-white leading-none" style={{ color: 'white' }}>
                                        {loading ? (
                                            <Skeleton.Input size="small" active />
                                        ) : (
                                            balance.current.toLocaleString()
                                        )}
                                    </Title>
                                    <Text className="text-blue-100 text-xs">pts</Text>
                                </div>
                            </div>
                        </div>
                        {balance.tier && (
                            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-inner flex items-center gap-5">
                                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl">
                                    <StarFilled />
                                </div>
                                <div>
                                    <Text className="text-blue-100 text-xs block mb-1">Member Status</Text>
                                    <Title level={2} className="m-0 text-white leading-none" style={{ color: 'white' }}>
                                        {balance.tier}
                                    </Title>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Visual accents */}
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl"></div>
            </div>

            {/* Catalog Content */}
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => {
                            setActiveTab(key);
                            if (key === 'history') fetchHistory();
                        }}
                        className="flex-1 custom-tabs"
                        items={[
                            {
                                key: 'catalog',
                                label: (
                                    <Space>
                                        <GiftOutlined /> Available Rewards
                                    </Space>
                                ),
                            },
                            {
                                key: 'history',
                                label: (
                                    <Space>
                                        <HistoryOutlined /> My Redemptions
                                    </Space>
                                ),
                            },
                        ]}
                    />

                    {activeTab === 'catalog' && (
                        <div className="flex gap-2">
                            <Input
                                placeholder="Search rewards..."
                                prefix={<SearchOutlined />}
                                className="w-full md:w-64 rounded-xl border-gray-200"
                                allowClear
                                onChange={(e) => onSearch(e.target.value)}
                            />
                            <Select
                                placeholder="Category"
                                className="w-40 rounded-xl"
                                allowClear
                                onChange={(v) => setFilters((prev) => ({ ...prev, category_id: v, page: 1 }))}
                            >
                                {options.categories?.map((cat) => (
                                    <Select.Option key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Select
                                placeholder="Brand"
                                className="w-40 rounded-xl"
                                allowClear
                                onChange={(v) => setFilters((prev) => ({ ...prev, brand_id: v, page: 1 }))}
                            >
                                {options.brands?.map((brand) => (
                                    <Select.Option key={brand.id} value={brand.id}>
                                        {brand.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </div>

                {activeTab === 'catalog' ? renderCatalog() : renderHistory()}
            </div>

            {/* Redemption Modal */}
            <Modal
                title={null}
                open={isRedeemModalOpen}
                onCancel={() => setIsRedeemModalOpen(false)}
                footer={null}
                centered
                closable={false}
                width={400}
                className="rounded-[32px] overflow-hidden"
            >
                {selectedReward && (
                    <div className="p-4 text-center">
                        <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <GiftOutlined style={{ fontSize: '32px', color: '#2563eb' }} />
                        </div>
                        <Title level={4}>Claim Reward?</Title>
                        <Text className="text-gray-500 block mb-6 px-4">
                            You are about to redeem <Text strong>{selectedReward.name}</Text> for{' '}
                            <Text strong className="text-blue-600">
                                {selectedReward.points_required}
                            </Text>{' '}
                            points.
                        </Text>

                        <div className="bg-gray-50 rounded-2xl p-4 mb-8 flex justify-between items-center text-sm">
                            <Text type="secondary">Remaining Balance</Text>
                            <Text strong>
                                {(balance.current - selectedReward.points_required).toLocaleString()} pts
                            </Text>
                        </div>

                        <div className="flex flex-col gap-3">
                            <Button
                                type="primary"
                                block
                                size="large"
                                className="h-12 rounded-2xl shadow-lg border-0 bg-blue-600 hover:bg-blue-700 font-semibold"
                                onClick={confirmRedeem}
                            >
                                Confirm & Redeem
                            </Button>
                            <Button
                                block
                                size="large"
                                type="text"
                                className="h-12 rounded-2xl text-gray-400"
                                onClick={() => setIsRedeemModalOpen(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            <style>{`
                .custom-tabs .ant-tabs-nav::before {
                    border-bottom-color: transparent !important;
                }
                .custom-tabs .ant-tabs-tab {
                    padding: 8px 16px !important;
                    transition: all 0.3s;
                    font-weight: 500;
                }
                .custom-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
                    color: #2563eb !important;
                }
                .custom-tabs .ant-tabs-ink-bar {
                    background: #2563eb !important;
                    height: 3px !important;
                    border-radius: 3px 3px 0 0;
                }
            `}</style>
        </div>
    );
};

export default RewardsCatalog;
